import { FieldValues, UseFormSetValue } from 'react-hook-form';
import { DEFAULT_MAX_ITEMS_PER_TRANSACTION } from '@constants/admissions';
import { Admission } from '@domain/admission';
import { CompanyRoles, ROLE_ADMIN } from '@domain/company';
import { Invitation } from '@domain/invitation';
import { TFunction } from 'i18next';

import { emailValidator, textValidator } from './constants';
import { isNumberAndNotIncludesPlus } from './is-number-and-not-includes-plus';
import { numberEndsWithPlus } from './number-ends-with-plus';
import { numberStartsWithPlus } from './number-starts-with-plus';

// Helper function to parse line parts
const parseLineParts = (parts: string[]) => {
  let nameSurname = '';
  let quantity = 1;
  let email: string | null = null;

  parts.forEach((part) => {
    if (part.includes('@')) {
      email = part;
    } else if (isNumberAndNotIncludesPlus(part)) {
      quantity = Number.parseInt(part, 10);
    } else if (numberEndsWithPlus(part)) {
      quantity = Number.parseInt(part.substring(0), 10) + 1;
    } else if (numberStartsWithPlus(part)) {
      quantity = Number.parseInt(part.substring(1), 10) + 1;
    } else {
      nameSurname += `${part} `;
    }
  });

  return { nameSurname: nameSurname.trim(), quantity, email };
};

// Helper function to validate line data
const validateLineData = (
  { nameSurname, quantity, email }: { nameSurname: string; quantity: number; email: string | null },
  {
    t,
    offer,
    roleName,
    enforceQuantityOne,
    enforceEmailRequired,
  }: {
    t: TFunction;
    offer?: Admission | Invitation;
    roleName?: CompanyRoles;
    enforceQuantityOne?: boolean;
    enforceEmailRequired?: boolean;
  }
) => {
  const lineErrors: string[] = [];

  // Name validation
  if (!nameSurname) {
    lineErrors.push(t('errors:missingNameSurname'));
  } else if (!textValidator.test(nameSurname)) {
    lineErrors.push(t('errors:invalidNameSurname'));
  }

  // Email validation
  if (email && !emailValidator.test(email)) {
    lineErrors.push(t('errors:emailFormFieldInvalidFormat'));
  }
  if (enforceEmailRequired && !email) {
    lineErrors.push(t('errors:emailFormFieldRequired'));
  }

  // Quantity validation
  if (Number.isNaN(quantity)) {
    lineErrors.push(t('errors:quantityMustBeNumber'));
  }
  if (enforceQuantityOne && quantity > 1) {
    lineErrors.push(t('errors:quantityMustBeOne'));
  }

  // Offer limits validation
  if (quantity && offer) {
    const isAdmin = roleName === ROLE_ADMIN;
    const maxQuantity = isAdmin ? DEFAULT_MAX_ITEMS_PER_TRANSACTION : offer.itemsPerTransaction.max;

    if (quantity > maxQuantity) {
      lineErrors.push(t('offers:admissionTicketQuantityMaxValue', { maxValue: maxQuantity }));
    }
    if (!isAdmin && quantity < offer.itemsPerTransaction.min) {
      lineErrors.push(t('offers:admissionTicketQuantityMinValue', { minValue: offer.itemsPerTransaction.min }));
    }
  }

  return lineErrors;
};

export const validateMultiInviteText = ({
  value,
  t,
  offer,
  setValue,
  roleName,
  enforceQuantityOne,
  enforceEmailRequired,
}: {
  value: string;
  t: TFunction;
  offer?: Admission | Invitation;
  setValue?: UseFormSetValue<FieldValues>;
  roleName?: CompanyRoles;
  enforceQuantityOne?: boolean;
  enforceEmailRequired?: boolean;
}): true | string => {
  const lines = value.trim().split('\n');
  const isEmpty = lines.length === 0 || (lines.length === 1 && lines[0].trim() === '');

  if (isEmpty) return t('errors:formFieldRequired');
  if (lines.length > 100) return t('errors:tooManyLines');

  const errors: string[] = [];
  let totalNumTickets = 0;

  lines.forEach((line, index) => {
    const parts = line.trim().split(/\s+/);
    const lineData = parseLineParts(parts);

    totalNumTickets += lineData.quantity;

    const lineErrors = validateLineData(lineData, { t, offer, roleName, enforceQuantityOne, enforceEmailRequired });

    if (lineErrors.length > 0) {
      errors.push(`${t('common:line')} ${index + 1}: ${lineErrors.join('. ')}`);
    }
  });

  if (setValue) setValue('quantity', totalNumTickets);

  return errors.length > 0 ? errors.join('\n') : true;
};
